import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Modal, Alert, Spinner } from 'react-bootstrap';
import { apiService } from '../../services/api';
import { CustomPayment, GetAppointmentsQuery } from '../../types';

const CustomPaymentsPage: React.FC = () => {
  const [customPayments, setCustomPayments] = useState<CustomPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState<CustomPayment | null>(null);
  const [showModal, setShowModal] = useState(false);

  const loadCustomPayments = async (page: number = 1, search: string = '') => {
    try {
      setLoading(true);
      setError('');
      
      const query: GetAppointmentsQuery = {
        page,
        limit: 10,
        search: search || undefined,
      };

      const response = await apiService.getCustomPayments(query);
      setCustomPayments(response.data || []);
      
      if (response.meta_data) {
        setTotalPages(response.meta_data.total_pages);
        setTotalCount(response.meta_data.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load custom payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomPayments();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadCustomPayments(1, searchTerm);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadCustomPayments(page, searchTerm);
  };

  const handleViewDetails = async (paymentId: string) => {
    try {
      const response = await apiService.getCustomPayment(paymentId);
      setSelectedPayment(response.data);
      setShowModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payment details');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Header className="modern-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">
                    <i className="bi bi-credit-card me-2"></i>
                    Custom Payments
                  </h4>
                  <p className="text-muted mb-0">
                    Manage custom payments from clients
                  </p>
                </div>
                <div className="text-muted">
                  Total: {totalCount} payments
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Search and Filters */}
              <Row className="mb-4">
                <Col md={6}>
                  <Form onSubmit={handleSearch}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="modern-input"
                      />
                      <Button 
                        type="submit" 
                        variant="primary"
                        className="modern-button"
                      >
                        <i className="bi bi-search me-1"></i>
                        Search
                      </Button>
                    </InputGroup>
                  </Form>
                </Col>
              </Row>

              {/* Error Alert */}
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              {/* Loading Spinner */}
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading custom payments...</p>
                </div>
              )}

              {/* Custom Payments Table */}
              {!loading && (
                <>
                  <div className="table-responsive">
                    <Table hover className="modern-table">
                      <thead className="modern-table-header">
                        <tr>
                          <th>Client</th>
                          <th>Email</th>
                          <th>Amount</th>
                          <th>File Number</th>
                          <th>Date</th>
                          <th>Transaction ID</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customPayments.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                              No custom payments found
                            </td>
                          </tr>
                        ) : (
                          customPayments.map((payment) => (
                            <tr key={payment._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-person me-2 text-muted"></i>
                                  <div>
                                    <div className="fw-semibold">
                                      {payment.first_name} {payment.last_name}
                                    </div>
                                    <small className="text-muted">
                                      <i className="bi bi-telephone me-1"></i>
                                      {payment.contact_no}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-envelope me-2 text-muted"></i>
                                  {payment.email}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-currency-dollar me-2 text-success"></i>
                                  <span className="fw-semibold text-success">
                                    {formatCurrency(payment.price)}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-file-text me-2 text-muted"></i>
                                  {payment.file_number || 'N/A'}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-calendar me-2 text-muted"></i>
                                  {formatDate(payment.date)}
                                </div>
                              </td>
                              <td>
                                <code className="text-muted small">
                                  {payment.charge_id}
                                </code>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleViewDetails(payment._id)}
                                  className="modern-button"
                                >
                                  <i className="bi bi-eye me-1"></i>
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav>
                        <ul className="pagination modern-pagination">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="page-link"
                            >
                              Previous
                            </Button>
                          </li>
                          
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                              <Button
                                variant={currentPage === page ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => handlePageChange(page)}
                                className="page-link"
                              >
                                {page}
                              </Button>
                            </li>
                          ))}
                          
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="page-link"
                            >
                              Next
                            </Button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Payment Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title>
            <i className="bi bi-credit-card me-2"></i>
            Payment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment && (
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-muted mb-3">Client Information</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Name</label>
                  <p className="form-control-plaintext">
                    {selectedPayment.first_name} {selectedPayment.last_name}
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <p className="form-control-plaintext">{selectedPayment.email}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Contact Number</label>
                  <p className="form-control-plaintext">{selectedPayment.contact_no}</p>
                </div>
              </div>
              <div className="col-md-6">
                <h6 className="text-muted mb-3">Payment Information</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Amount</label>
                  <p className="form-control-plaintext text-success fw-bold">
                    {formatCurrency(selectedPayment.price)}
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">File Number</label>
                  <p className="form-control-plaintext">{selectedPayment.file_number || 'N/A'}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Transaction ID</label>
                  <p className="form-control-plaintext">
                    <code>{selectedPayment.charge_id}</code>
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Payment Date</label>
                  <p className="form-control-plaintext">
                    {formatDate(selectedPayment.date)}
                  </p>
                </div>
              </div>
              <div className="col-12">
                <h6 className="text-muted mb-3">Additional Information</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Notes</label>
                  <p className="form-control-plaintext">
                    {selectedPayment.additional_info || 'No additional information provided'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            className="modern-button"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomPaymentsPage;
